"use client";

import { useEffect } from "react";

interface BuildError {
  message: string;
  file?: string;
  line?: number;
  column?: number;
  stack?: string;
  type: string;
}

export function BuildErrorReporter() {
  useEffect(() => {
    // Only run in development mode
    if (process.env.NODE_ENV !== "development") {
      return;
    }

    // Function to send build errors to parent window
    const sendBuildErrorToParent = (error: BuildError) => {
      if (window.parent && window.parent !== window) {
        try {
          window.parent.postMessage(
            {
              type: "IFRAME_ERROR",
              error: {
                error: error.message,
                stack: error.stack,
                filename: error.file,
                lineno: error.line,
                colno: error.column,
                timestamp: new Date().toISOString(),
                type: "build",
              },
            },
            "*"
          );
        } catch (e) {
          console.warn("Failed to send build error to parent:", e);
        }
      }
    };

    // Override console.error to catch build errors
    const originalConsoleError = console.error;
    console.error = (...args) => {
      // Call original console.error
      originalConsoleError.apply(console, args);

      // Check if this looks like a build error
      const errorMessage = args.join(" ");
      sendBuildErrorToParent({
        message: errorMessage,
        type: "build",
        stack: new Error().stack,
      });
    };

    // Listen for unhandled errors that might be build-related
    const handleError = (event: ErrorEvent) => {
      const error = event.error;
      if (
        error &&
        (error.name === "SyntaxError" ||
          error.message.includes("Unexpected token"))
      ) {
        sendBuildErrorToParent({
          message: error.message,
          file: event.filename,
          line: event.lineno,
          column: event.colno,
          stack: error.stack,
          type: "build",
        });
      }
    };

    window.addEventListener("error", handleError);

    // Listen for Next.js specific errors
    const handleNextError = (event: any) => {
      if (event.detail && event.detail.error) {
        const error = event.detail.error;
        sendBuildErrorToParent({
          message: error.message || "Next.js build error",
          stack: error.stack,
          type: "build",
        });
      }
    };

    // Listen for custom Next.js error events
    window.addEventListener("nextjs-build-error", handleNextError);

    // Cleanup
    return () => {
      console.error = originalConsoleError;
      window.removeEventListener("error", handleError);
      window.removeEventListener("nextjs-build-error", handleNextError);
    };
  }, []);

  return null; // This component doesn't render anything
}
